import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';

export default function AboutPage() {
  const { t } = useLanguage();
  const a = t.about;

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/custom/about-hero.webp"
            alt="Young Mass Sport athletes"
            className="w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 text-center px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm text-xs tracking-[6px] text-[#CE2B37] uppercase mb-4"
          >
            {a.hero.overline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white tracking-tight max-w-4xl"
          >
            {a.hero.title}
          </motion.h1>
        </div>
      </section>

      <TricolorStripe />

      {/* Origin Story */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="origin-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight">
                {a.origin.title}
              </h2>
              <p className="font-dm text-base text-[#6B6B6B] mt-6 leading-relaxed">
                {a.origin.p1}
              </p>
              <p className="font-dm text-base text-[#6B6B6B] mt-4 leading-relaxed">
                {a.origin.p2}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-[400px]"
            >
              <img
                src="/custom/montreal-story.webp"
                alt="Mass Sport jersey unveiling"
                className="absolute inset-0 w-full h-full object-cover grayscale"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-[#0D0D0D]" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl text-white tracking-tight text-center mb-16"
          >
            {a.timeline.title}
          </motion.h2>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-[#CE2B37]/30 -translate-x-1/2" />

            <div className="space-y-12 md:space-y-0">
              {a.timeline.events.map((event, i) => (
                <motion.div
                  key={event.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className={`md:grid md:grid-cols-2 md:gap-16 ${i % 2 === 0 ? '' : 'md:direction-rtl'}`}
                >
                  <div className={`${i % 2 === 0 ? 'md:text-right md:pr-16' : 'md:col-start-2 md:pl-16 md:text-left'} relative`}>
                    {/* Dot */}
                    <div className="hidden md:block absolute top-2 w-4 h-4 bg-[#CE2B37] rounded-full" 
                         style={{ [i % 2 === 0 ? 'right' : 'left']: '-2rem' }} />
                    
                    <span className="font-bebas text-5xl text-[#CE2B37]/50">
                      {event.year}
                    </span>
                    <p className="font-dm text-sm text-white/70 mt-4 leading-relaxed">
                      {event.text}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Italian Partner */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="partner-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-[400px] order-2 lg:order-1"
            >
              <img
                src="https://images.pexels.com/photos/46798/the-ball-stadion-football-the-pitch-46798.jpeg?w=800"
                alt="Stadium"
                className="absolute inset-0 w-full h-full object-cover grayscale"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <p className="font-dm text-xs tracking-[6px] text-[#CE2B37] uppercase mb-4">
                {a.partner.overline}
              </p>
              <h2 className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight">
                {a.partner.title}
              </h2>
              <p className="font-dm text-base text-[#6B6B6B] mt-6 leading-relaxed">
                {a.partner.body}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight text-center mb-16"
          >
            {a.values.title}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {a.values.items.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white border border-[#E8E8E8] p-8"
              >
                <h3 className="font-bebas text-2xl text-[#0D0D0D] tracking-wide mb-4">
                  {item.title}
                </h3>
                <p className="font-dm text-sm text-[#6B6B6B] leading-relaxed">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Location */}
      <section className="py-24 md:py-32 bg-[#0D0D0D]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-bebas text-4xl md:text-5xl text-white tracking-tight">
                {a.location.title}
              </h2>
              <div className="mt-8 space-y-4">
                <p className="font-dm text-base text-white font-medium">
                  DM Sports Mass Canada Inc.
                </p>
                <p className="font-dm text-sm text-white/70" data-testid="about-business-address">
                  4003 Boulevard Robert<br />
                  {t.footer.address[1]}
                </p>
                <p className="font-dm text-sm text-white/70 mt-6">
                  {a.location.description}
                </p>
                <p className="font-dm text-sm text-white/70">
                  {a.location.hours}
                </p>
              </div>
              <a
                href="https://maps.google.com/?q=4003+Boulevard+Robert,+Montr%C3%A9al,+QC+H1Z+4H6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-dm text-sm text-[#CE2B37] mt-6 hover:underline"
                data-testid="get-directions-link"
              >
                <MapPin className="w-4 h-4" />
                {a.location.directions} →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-[400px] bg-[#1A1A1A] overflow-hidden"
            >
              <iframe
                title="DM Sports Mass Canada — Montréal"
                src="https://www.google.com/maps?q=4003+Boulevard+Robert,+Montr%C3%A9al,+QC+H1Z+4H6&output=embed"
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="about-google-map"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
